import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import { SITE_URL, SITE_NAME } from '@/lib/seo';

interface SeoData {
  title: string;
  description: string;
  keywords: string;
  path: string;
  type?: string;
  image?: string;
}

function resolveSeo(pathname: string): SeoData | null {
  if (pathname === '/') {
    return {
      title: 'AllChinaBuy Spreadsheet — Products, Sellers & Buying Guide 2026',
      description:
        'Discover the AllChinaBuy Spreadsheet resource hub. Find products, sellers, QC guides, shipping tips, and buying guides for AllChinaBuy shopping. Updated 2026.',
      keywords:
        'AllChinaBuy spreadsheet, AllChinaBuy guide, AllChinaBuy review, AllChinaBuy shipping, AllChinaBuy sellers, AllChinaBuy products',
      path: '/',
      type: 'website',
      image:
        'https://readdy.ai/api/search-image?query=Aerial%20view%20of%20luxury%20fashion%20boutique%20interior%20with%20warm%20amber%20lighting%20and%20curated%20clothing%20displays%20on%20wooden%20shelves%20minimal%20elegant%20editorial%20photography%20soft%20natural%20light%20warm%20tones%20high-end%20retail%20atmosphere%20no%20people&width=1920&height=1080&seq=hero-bg-01&orientation=landscape',
    };
  }

  if (pathname === '/allchinabuy-spreadsheet-guide') {
    return {
      title: 'The Complete AllChinaBuy Spreadsheet Guide 2026',
      description:
        'A comprehensive guide to AllChinaBuy spreadsheet resources: how to read them, find reliable sellers, understand categories, and ship internationally with confidence.',
      keywords:
        'AllChinaBuy spreadsheet guide, how to use AllChinaBuy, AllChinaBuy tutorial, AllChinaBuy shipping, AllChinaBuy sellers',
      path: '/allchinabuy-spreadsheet-guide',
      type: 'article',
      image:
        'https://readdy.ai/api/search-image?query=Minimal%20workspace%20with%20laptop%20spreadsheet%20screen%20warm%20wooden%20desk%20soft%20natural%20lighting%20coffee%20cup%20notebook%20pen%20clean%20aesthetic%20editorial%20photography%20warm%20beige%20tones%20no%20people&width=1920&height=600&seq=article-hero-01&orientation=landscape',
    };
  }

  const match = pathname.match(/^\/articles\/([^/]+)$/);
  if (match) {
    const article = articles.find((a) => a.slug === match[1]);
    if (article) {
      return {
        title: `${article.title} — AllChinaBuy Spreadsheet`,
        description: article.subtitle,
        keywords: `${article.category}, AllChinaBuy, AllChinaBuy spreadsheet, AllChinaBuy guide`,
        path: `/articles/${article.slug}`,
        type: 'article',
        image: article.heroImage,
      };
    }
  }

  return null;
}

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function removeMeta(attr: 'name' | 'property', key: string) {
  const el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (el) el.remove();
}

function upsertCanonical(href: string) {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export default function SeoManager() {
  const { pathname } = useLocation();

  useEffect(() => {
    const seo = resolveSeo(pathname);
    if (!seo) return;

    const url = seo.path === '/' ? `${SITE_URL}/` : `${SITE_URL}${seo.path}`;

    document.title = seo.title;
    upsertMeta('name', 'description', seo.description);
    upsertMeta('name', 'keywords', seo.keywords);
    upsertCanonical(url);
    upsertMeta('property', 'og:title', seo.title);
    upsertMeta('property', 'og:description', seo.description);
    upsertMeta('property', 'og:type', seo.type || 'website');
    upsertMeta('property', 'og:url', url);
    upsertMeta('property', 'og:site_name', SITE_NAME);
    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', seo.title);
    upsertMeta('name', 'twitter:description', seo.description);

    if (seo.image) {
      upsertMeta('property', 'og:image', seo.image);
      upsertMeta('name', 'twitter:image', seo.image);
    } else {
      removeMeta('property', 'og:image');
      removeMeta('name', 'twitter:image');
    }
  }, [pathname]);

  return null;
}